import { Bubble } from 'js/bubbleCore/bubbles/types';
import { IBouncer, IPointZone, IShooterData, Place, IBubbleGrid } from 'js/bubbleCore/types';
import { IPoint } from 'framework/utils/math-utils';
import { Signal } from 'framework/utils/Signal';

export enum Wall {
  Left,
  Right,
  Top,
  Bottom,
}

export enum BubbleDestroyReason {
  PointZone,
  TopCollision,
}

export enum EventType {
  ShootingSpaceGridCollision,
  ShooterUpdated,
  BubbleBouncerCollision,
  BubbleDestroyed,
  BubbleTransfered,
  BubbleUpdated,
  BubbleSpaceLimitCollision,
  BubblePointZoneCollision,
  IncreasedScore,
  GridOffsetChanged,
  SuccessfulMove,
  UnsuccessfulMove,
  DeathLineCrossed,
  ChainChanged,
}

export interface IDeathLineCrossedData {

}

export interface IChainChangedData {
  newValue: number;
  bonusGranted: boolean;
}

export interface ISuccessfulMoveData {
  bubblesToPopout: Bubble[];
}

export interface IUnsuccessfulMoveData {
  shootedBubble: Bubble;
}

export interface IIncreasedScoreData {
  amount: number;
}

export interface IBubblePointZoneCollisionData {
  bubble: Bubble;
  pointZone: IPointZone;
}

export interface IBubbleBouncerCollisionData {
  bubble: Bubble;
  bouncer: IBouncer;
}

export interface IBubbleSpaceLimitCollisionData {
  bubble: Bubble;
  wall: Wall;
  collisionPoint: IPoint;
}

export interface IBubbleDestroyedData {
  bubble: Bubble;
  reason: BubbleDestroyReason;
}

export interface IBubbleUpdatedData {
  bubble: Bubble;
}

export interface IShooterUpdatedData {
  shooter: IShooterData;
}

export interface IShootingSpaceGridCollisionData {
  bubble: Bubble;
  colliders: Bubble[];
}

export interface IBubbleTransferedData {
  bubble: Bubble;
  toPlace: Place;
  fromPlace: Place | null;
}

export interface IGridOffsetChangedData {
  grid: IBubbleGrid;
}

interface EventTypeToEventData {
  [EventType.BubbleBouncerCollision]: IBubbleBouncerCollisionData;
  [EventType.BubblePointZoneCollision]: IBubblePointZoneCollisionData;
  [EventType.BubbleSpaceLimitCollision]: IBubbleSpaceLimitCollisionData;
  [EventType.BubbleDestroyed]: IBubbleDestroyedData;
  [EventType.BubbleUpdated]: IBubbleUpdatedData;
  [EventType.ShooterUpdated]: IShooterUpdatedData;
  [EventType.ShootingSpaceGridCollision]: IShootingSpaceGridCollisionData;
  [EventType.BubbleTransfered]: IBubbleTransferedData;
  [EventType.IncreasedScore]: IIncreasedScoreData;
  [EventType.GridOffsetChanged]: IGridOffsetChangedData;
  [EventType.SuccessfulMove]: ISuccessfulMoveData;
  [EventType.UnsuccessfulMove]: IUnsuccessfulMoveData;
  [EventType.DeathLineCrossed]: IDeathLineCrossedData;
  [EventType.ChainChanged]: IChainChangedData;
}

export interface IEvent<T extends EventType = EventType> {
  type: T,
  data: EventData<T>,
}

export type EventData<T extends EventType = EventType> = EventTypeToEventData[T];

/// helpers

export function createChainChangedEvent(newValue: number, bonusGranted: boolean): IEvent<EventType.ChainChanged> {
  return { type: EventType.ChainChanged, data: { newValue, bonusGranted }};
}

export function createDeathLineCrossedEvent(): IEvent<EventType.DeathLineCrossed> {
  return { type: EventType.DeathLineCrossed, data: {} };
}

export function createSuccessfulMoveEvent(
  bubblesToPopout: Bubble[],
): IEvent<EventType.SuccessfulMove> {
  return { type: EventType.SuccessfulMove, data: { bubblesToPopout }};
}

export function createUnsuccessfulMoveEvent(
  shootedBubble: Bubble,
): IEvent<EventType.UnsuccessfulMove> {
  return { type: EventType.UnsuccessfulMove, data: { shootedBubble }};
}

export function createGridOffsetChangedEvent(
  grid: IBubbleGrid,
): IEvent<EventType.GridOffsetChanged> {
  return { type: EventType.GridOffsetChanged, data: {grid} };
}

export function createBubblePointZoneCollisionEvent (
  bubble: Bubble, pointZone: IPointZone,
): IEvent<EventType.BubblePointZoneCollision> {
  return { type: EventType.BubblePointZoneCollision, data: {bubble, pointZone} };
}

export function createBubbleBouncerCollisionEvent(
  bubble: Bubble, bouncer: IBouncer,
): IEvent<EventType.BubbleBouncerCollision> {
  return {type: EventType.BubbleBouncerCollision, data: {bubble, bouncer}};
}

export function createBubbleSpaceLimitCollisionEvent(
  bubble: Bubble,
  wall: Wall,
  collisionPoint: IPoint,
): IEvent<EventType.BubbleSpaceLimitCollision> {
  return {type: EventType.BubbleSpaceLimitCollision, data: {bubble, wall, collisionPoint}};
}

export function createBubbleUpdatedEvent(bubble: Bubble): IEvent<EventType.BubbleUpdated> {
  return {type: EventType.BubbleUpdated, data: {bubble}};
}

export function createShooterUpdatedEvent(
  shooter: IShooterData
): IEvent<EventType.ShooterUpdated> {
  return {type: EventType.ShooterUpdated, data: {shooter}};
}

export function createShootingSpaceGridCollisionEvent(
  bubble: Bubble,
  colliders: Bubble[],
): IEvent<EventType.ShootingSpaceGridCollision> {
  return {type: EventType.ShootingSpaceGridCollision, data: {bubble, colliders}};
}

export function createBubbleTransferEvent(
  bubble: Bubble, toPlace: Place, fromPlace: Place | null
): IEvent<EventType.BubbleTransfered> {
  return {type: EventType.BubbleTransfered, data: {bubble, toPlace, fromPlace}};
}

export function createBubbleDestroyedEvent(
  bubble: Bubble,
  reason: BubbleDestroyReason,
): IEvent<EventType.BubbleDestroyed> {
  return {type: EventType.BubbleDestroyed, data: {bubble, reason}};
}

export function createIncreasedScoreEvent(
  amount: number,
): IEvent<EventType.IncreasedScore> {
  return {type: EventType.IncreasedScore, data: {amount}};
}

//

export interface IHasEventHandlers {
  eventHandlers: EventHandlers;
}

export type EventHandlers<T extends EventType = EventType> = {
  [key in T]?: EventHandler<key>;
}

export type EventHandler<T extends EventType = EventType> = (event: IEvent<T>) => void;

export function connectSignalToHasEventHandlers(
  signal: Signal<IEvent>,
  hasHandlers: IHasEventHandlers[],
): void {
  signal.add((event) => {
    hasHandlers.forEach((object) => {
      const handler = object.eventHandlers[event.type];
      // check why it is casting to never
      // @ts-ignore
      if (handler) handler(event);
    });
  });
}

// casts

export function filterEvents<T extends EventType>(events: IEvent[], type: T): IEvent<T>[] {
  return events.filter((e) => e.type === type) as IEvent<T>[];
}